import { useMutation, useQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AccessNotice } from '@/components/AccessNotice/AccessNotice';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Icon } from '@/components/Icon/Icon';
import {
  OrganizationForm,
  type OrganizationDraft,
} from '@/components/OrganizationForm/OrganizationForm';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { RecordList } from '@/components/RecordList/RecordList';
import { Spinner } from '@/components/Spinner/Spinner';
import { TextField } from '@/components/TextField/TextField';
import {
  CREATE_ORGANIZATION,
  ORGANIZATIONS_QUERY,
  type CreateOrganizationResult,
  type OrganizationsResult,
} from '@/graphql/tenants';
import { friendlyError, isPermissionDenied } from '@/lib/keystone-errors';
import { displayName, matchesSearch, sortByName, tenantCount } from '@/lib/tenants';
import { useResourceAccess } from '@/lib/use-resource-access';
import { useToast } from '@/toast/useToast';

import styles from './TenantsContainer.module.css';

/** `/organizations`: every umbrella organization; Full adds one. */
export function OrganizationsContainer() {
  const toast = useToast();
  const navigate = useNavigate();
  const { access, refetch: recheckAccess } = useResourceAccess('tenants');
  const canRead = access === 'read' || access === 'full';
  const canEdit = access === 'full';

  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data, loading, error } = useQuery<OrganizationsResult>(ORGANIZATIONS_QUERY, {
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });
  const [createOrganization, { loading: saving }] =
    useMutation<CreateOrganizationResult>(CREATE_ORGANIZATION);

  const organizations = useMemo(
    () =>
      sortByName(
        (data?.organizations ?? []).filter((organization) => matchesSearch(organization, search)),
      ).map((organization) => ({
        id: organization.id,
        title: displayName(organization),
        meta: tenantCount(organization.tenants.length),
      })),
    [data, search],
  );

  if (!canRead) {
    return (
      <AccessNotice area="organizations" onRetry={recheckAccess} state={access as 'checking'} />
    );
  }

  const onCreate = async (draft: OrganizationDraft) => {
    setCreateError(null);
    try {
      const result = await createOrganization({
        variables: { input: draft },
        refetchQueries: [ORGANIZATIONS_QUERY],
      });
      setCreating(false);
      toast.success({ title: `${draft.name} added`, description: 'Now link its tenants.' });
      const id = result.data?.createOrganization.id;
      if (id) {
        navigate(`/organizations/${id}`);
      }
    } catch (cause) {
      setCreateError(friendlyError(cause));
      if (isPermissionDenied(cause)) {
        recheckAccess();
      }
    }
  };

  const total = data?.organizations.length ?? 0;
  const addButton = canEdit ? (
    <Button icon={<Icon name="plus" />} onClick={() => setCreating(true)}>
      Add organization
    </Button>
  ) : null;

  return (
    <div className={styles.page}>
      <PageHeader
        actions={addButton}
        lede="The parent companies above one or more tenants."
        title="Organizations"
      />

      {total > 0 ? (
        <div className={styles.filters}>
          <div className={styles.search}>
            <TextField
              label="Search"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, slug or app-api id"
              type="search"
              value={search}
            />
          </div>
        </div>
      ) : null}

      {error && !data ? (
        <Callout tone="danger" title="Organizations couldn't be loaded">
          {friendlyError(error)}
        </Callout>
      ) : loading && !data ? (
        <p className={styles.loading} role="status">
          <Spinner /> Loading organizations…
        </p>
      ) : total === 0 ? (
        <EmptyState action={addButton} icon="buildings" title="No organizations yet">
          <p>Organizations you add appear here.</p>
        </EmptyState>
      ) : organizations.length === 0 ? (
        <EmptyState icon="magnifying-glass" title="No organizations match">
          <p>Try a different name, slug or id.</p>
        </EmptyState>
      ) : (
        <RecordList
          items={organizations}
          label="Organizations"
          renderLink={(item, children) => <Link to={`/organizations/${item.id}`}>{children}</Link>}
        />
      )}

      {creating ? (
        <OrganizationForm
          busy={saving}
          error={createError}
          onCancel={() => {
            setCreating(false);
            setCreateError(null);
          }}
          onSubmit={onCreate}
        />
      ) : null}
    </div>
  );
}
